import { Trans, useTranslation } from 'next-i18next'
import { allPass, includes, reject } from 'ramda'

import {
  Heading2,
  LinkGrid,
  LinkText,
  Section2
} from '@/features/Listings/seo/elements'
import { Label } from '@/components/text'

interface PopularUniversitiesProps {
  state: string
  locationName: string
  neighbourhoodSlug: string
  citySlug: string
}

type LinkArray = string[][]

const PopularUniversities = ({
  state,
  locationName,
  neighbourhoodSlug,
  citySlug
}: PopularUniversitiesProps) => {
  const { t } = useTranslation()

  const UNI_LINKS_AB: LinkArray = [
    ['calgary', 'university-of-calgary'],
    ['edmonton', 'university-of-alberta'],
    ['calgary', 'mount-royal-university'],
    ['lethbridge', 'university-of-lethbridge'],
    ['edmonton', 'macewan-university']
  ]

  const UNI_LINKS_BC: LinkArray = [
    ['vancouver', 'ubc', 'UBC (Vancouver)'],
    ['burnaby', 'sfu'],
    ['burnaby', 'bcit'],
    ['north-vancouver', 'capilano-university'],
    ['vancouver', 'vcc'],
    ['vancouver', 'langara-college'],
    ['vancouver', 'emily-carr-university'],
    ['vancouver', 'vfs'],
    ['victoria', 'uvic'],
    ['surrey', 'sfu', 'SFU (Surrey)'],
    ['kelowna', 'ubc-okanagan'],
    ['kamloops', 'thompson-rivers-university'],
    ['langley', 'kpu', 'KPU (Langley)'],
    ['surrey', 'kpu', 'KPU (Surrey)'],
    ['richmond', 'kpu', 'KPU (Richmond)'],
    ['victoria', 'royal-roads-university']
  ]

  const UNI_LINKS_MB: LinkArray = [
    ['winnipeg', 'university-of-winnipeg'],
    ['winnipeg', 'university-of-manitoba'],
    ['winnipeg', 'universite-de-saint-boniface'],
    ['winnipeg', 'booth-university-college'],
    ['winnipeg', 'canadian-mennonite-university'],
    ['winnipeg', 'menno-simons-college'],
    ['winnipeg', 'red-river-college', 'Red River College (Notre Dame Campus)'],
    [
      'selkirk',
      'red-river-college',
      'Red River College (Interlake and Peguis - Fisher River Campus)'
    ],
    ['winkler', 'red-river-college', 'Red River College (Winkler Campus)'],
    ['steinbach', 'red-river-college', 'Red River College (Steinbach Campus)'],
    ['winnipeg', 'manitoba-institute-of-trades-technology'],
    ['winnipeg', 'robertson-college']
  ]

  const UNI_LINKS_NS: LinkArray = [
    ['halifax', 'dalhousie-university'],
    ['wolfville', 'acadia-university'],
    ['halifax', 'saint-marys-university'],
    ['sydney', 'cape-breton-university'],
    ['antigonish', 'st-francis-xavier-university'],
    ['halifax', 'nscad-university'],
    ['halifax', 'university-of-kings-college'],
    ['halifax', 'nova-scotia-community-college'],
    ['halifax', 'atlantic-school-of-theology'],
    ['halifax', 'mount-saint-vincent-university']
  ]

  const UNI_LINKS_ON: LinkArray = [
    ['toronto', 'university-of-toronto'],
    ['waterloo', 'university-of-waterloo'],
    ['north-york', 'york-university'],
    ['ottawa', 'carleton-university'],
    ['kingston', 'queens-university'],
    ['ottawa', 'university-of-ottawa'],
    [
      'scarborough',
      'university-of-toronto',
      'University of Toronto (Scarborough)'
    ],
    ['toronto', 'toronto-metropolitan-university'],
    ['london', 'western-university'],
    ['hamilton', 'mcmaster-university'],
    ['guelph', 'university-of-guelph'],
    ['st-catharines', 'brock-university'],
    ['oshawa', 'ontario-tech-university'],
    ['waterloo', 'wilfrid-laurier-university']
  ]

  const UNI_LINKS_QC: LinkArray = [
    ['montreal', 'mcgill-university'],
    ['quebec-city', 'laval-university'],
    ['montreal', 'concordia-university'],
    ['montreal', 'university-of-montreal'],
    ['sherbrooke', 'universite-de-sherbrooke']
  ]

  const STATE_LINKS: Record<string, LinkArray> = {
    AB: UNI_LINKS_AB,
    BC: UNI_LINKS_BC,
    MB: UNI_LINKS_MB,
    NS: UNI_LINKS_NS,
    ON: UNI_LINKS_ON,
    QC: UNI_LINKS_QC
  }

  const ALL_LINKS: LinkArray = [
    ...UNI_LINKS_AB,
    ...UNI_LINKS_BC,
    ...UNI_LINKS_MB,
    ...UNI_LINKS_NS,
    ...UNI_LINKS_ON,
    ...UNI_LINKS_QC
  ]

  const UNI_LINKS = STATE_LINKS[state] || ALL_LINKS
  const linksWithoutCurrent = reject(
    allPass([includes(neighbourhoodSlug), includes(citySlug)]),
    UNI_LINKS
  )

  const hasNoLinks = linksWithoutCurrent.length === 0

  if (hasNoLinks) return null

  return (
    <Section2>
      <Heading2>
        <Trans
          i18nKey="seo:Popular Universities Near LOCATION"
          values={{
            location: locationName
          }}
          defaults="Popular Universities Near {{ location }}"
        />
      </Heading2>
      <LinkGrid linksOnly>
        {linksWithoutCurrent.map(uni => {
          const citySlug = uni[0]
          const uniSlug = uni[1]
          const uniDisplayName = uni[2]
          const uniName =
            uniDisplayName || t(`seo:neighbourhood_name_${uniSlug}_${citySlug}`)
          return (
            <Label key={`${uniSlug}-${citySlug}`} fontSize="14px">
              <LinkText href={`/rental-listings/city/${citySlug}/${uniSlug}`}>
                <Trans
                  i18nKey="seo:Apartments for rent near LOCATION"
                  values={{ location: uniName }}
                  defaults="Apartments for rent near {{ location }}"
                />
              </LinkText>
            </Label>
          )
        })}
      </LinkGrid>
    </Section2>
  )
}

export default PopularUniversities
